import React, {
  forwardRef,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { find, isEmpty, map, split } from "lodash";
import { FieldTypes } from "utils/formUtils";
import message from "components/CustomMessage";
import GenericMappingForm from "../GenericMappingForm";
import { Form } from "antd";
import { useFeatureFlag } from "contexts/FeatureFlagContext";

export const scanTypes = [
  {
    label: "SCA",
    value: "SCA",
  },
  {
    label: "Secrets",
    value: "Secrets",
  },
  {
    label: "Container Security",
    value: "Container Security",
  },
  {
    label: "IaC",
    value: "IaC",
  },
  {
    label: "SAST",
    value: "SAST",
  },
  {
    label: "Misconfiguration",
    value: "Misconfiguration",
  },
  {
    label: "License",
    value: "License",
  },
];

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
};

function OrcaSecurityShiftLeftMappingForm(
  { disableProjectFields = false, ...props },
  ref
) {
  const featureFlags = useFeatureFlag();

  const [projects, setProjects] = useState(() => {
    if (props.record) {
      const { projectId, projectName } = props.record;
      return [{ label: projectName, value: projectId }];
    }
    return [];
  });
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues, scanTypes: ["SCA", "Secrets"] }];
  });
  const [form] = Form.useForm();
  const apiParamRef = useRef({});
  const [repositoriesList, setRepositoriesList] = useState(() => {
    if (props.record) {
      const { repositories, projectId, projectName } = props.record;
      return {
        [`${projectId}:${projectName}`]: map(
          split(repositories, ","),
          (repo) => ({ label: repo, value: repo })
        ),
      };
    }
    return {};
  });

  const fetchProjects = useCallback(
    (params) => {
      const result = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        ...params,
        login_id: props.loginConfigId,
      });
      const { cancel } = result;
      const promise = result.promise
        .then((res) => {
          setProjects(res.projects);
          return res.projects;
        })
        .catch((err) => message.error(err));
      return { promise, cancel };
    },
    [props.toolName, props.loginConfigId]
  );

  const fetchRepos = useCallback(
    (params) => {
      const result = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        ...params,
        login_id: props.loginConfigId,
      });
      const { cancel } = result;
      const promise = result.promise
        .then((res) => {
          setRepositoriesList((prev) => ({
            ...prev,
            [params.project]: res.projects,
          }));
          return res.projects;
        })
        .catch((err) => message.error(err));
      return { promise, cancel };
    },
    [props.toolName, props.loginConfigId]
  );

  const fetchBranches = useCallback(
    (params) => {
      const result = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        ...params,
        login_id: props.loginConfigId,
      });
      const { cancel } = result;
      const promise = result.promise
        .then((res) => {
          return res.projects;
        })
        .catch((err) => message.error(err));
      return { promise, cancel };
    },
    [props.toolName, props.loginConfigId]
  );

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { projectId, repositories, scanTypes } = config;
    return {
      projectId,
      repositories: isEmpty(repositories)
        ? undefined
        : split(repositories, ","),
      scanTypes: scanTypes.length === 0 ? null : split(scanTypes, ","),
      //send the property received in config
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    };
  }, []);

  const getExtraFieldsData = (config) => {
    const { projectId, repositories, branch, scanTypes } = config;
    const projectName = find(projects, { id: projectId })?.name;
    return {
      projectId,
      projectName,
      branch,
      repositories: !isEmpty(repositories)
        ? Array.isArray(repositories)
        ? repositories.join(",")
        : repositories
        : map(
            repositoriesList[`${projectId}:${projectName}`],
            (repo) => repo.name
          ).join(),
      scanTypes: scanTypes ? scanTypes.join() : "",
      //send the property received in config
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    };
  };

  const onFieldChange = useCallback(
    (value, field, type) => {
      const { name } = field;
      const configurations = form.getFieldValue("configurations");
  
      const resetFields = { branch: undefined };
      if (type === "project") {
        resetFields.repositories = undefined;
      }
  
      configurations[name] = {
        ...configurations[name],
        ...resetFields,
      };
  
      form.setFieldsValue({ configurations });
    },
    [form]
  );
  

  const isFieldDisabled = useCallback(
    (fieldKey, form, type) => {
      const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
      if (!fieldConfiguration || !fieldConfiguration.projectId) {
        return true;
      }
      if (type === "repository" && disableProjectFields) {
        return true;
      }
      if (type === "branch" && !fieldConfiguration.repositories) {
        return true;
      }
      return false;
    },
    [disableProjectFields]
  );

  const extraFields = useMemo(
    () => [
      {
        name: "projectId",
        colSpan: "10",
        type: FieldTypes.SELECT,
        label: "Project",
        required: true,
        disabled: disableProjectFields,
        props: {
          onChange: (value, field) => onFieldChange(value, field, "project"),
          autoFocus: !disableProjectFields,
          api: fetchProjects,
        },
      },
      {
        name: "repositories",
        colSpan: "12",
        type: FieldTypes.SELECT,
        label: "Repository",
        propsGenerator: (fieldKey) => ({
          disabled: isFieldDisabled(fieldKey, form, "repository"),
        }),
        disabled: disableProjectFields,
        props: {
          onChange: (value, field) => onFieldChange(value, field, "repository"),
          setExtraProps: (field, listField) => {
            const allConfigs = form.getFieldsValue().configurations;
            const apiParams = apiParamRef.current;
            const projectID = allConfigs[listField.name]?.projectId;
            const projectName = find(projects, { id: projectID })?.name;
            const projectStr = `${projectID}:${projectName}`;

            const keyName = `${listField.name}_${projectStr}`;
            if (!apiParams[keyName] && projectID) {
              apiParams[keyName] = { project: projectStr };
            }
            return {
              api: projectID && fetchRepos,
              apiParams: apiParams[keyName],
              resetOptionsProp: apiParams[keyName],
            };
          },
        },
      },
      ...(featureFlags.ORCA_SECURITY_SHIFTLEFT_BRANCH_DISABLED ? [] : [
      {
        name: "branch",
        colSpan: "12",
        type: FieldTypes.SELECT,
        label: "Branch",
        help: "If no branch is selected, latest scan for the repository will be fetched.",
        propsGenerator: (fieldKey) => ({
          disabled: isFieldDisabled(fieldKey, form,"branch"),
        }),
        disabled: disableProjectFields,
        props: {
          setExtraProps: (field, listField) => {
            const allConfigs = form.getFieldsValue().configurations;
            const apiParams = apiParamRef.current;
            const projectID = allConfigs[listField.name]?.projectId;
            const projectName = find(projects, { id: projectID })?.name;
            const projectStr = `${projectID}:${projectName}`;
            const repository = allConfigs[listField.name]?.repositories;

            const keyName = `${listField.name}_${projectStr}_${repository}`;
            if (!apiParams[keyName] && projectID && repository) {
              apiParams[keyName] = { project: projectStr, name: repository };
            }
            return {
              api: projectID && repository && fetchBranches,
              apiParams: apiParams[keyName],
              resetOptionsProp: apiParams[keyName],
            };
          },
        },
      },
    ]),
      {
        name: "scanTypes",
        colSpan: "10",
        type: FieldTypes.MULTI_SELECT,
        label: "Scan Type",
        placeholder: "Leave blank to select all scan types",
        options: scanTypes,
          help: "Branch filter will not be considered for Misconfiguration and License findings.",
      },
    ],
    [disableProjectFields, fetchProjects, onFieldChange, isFieldDisabled, form, projects, fetchRepos, fetchBranches, featureFlags.ORCA_SECURITY_SHIFTLEFT_BRANCH_DISABLED]
  );

  return (
    <GenericMappingForm
      {...props}
      form={form}
      ref={ref}
      projects={projects}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
    />
  );
}

export default forwardRef(OrcaSecurityShiftLeftMappingForm);
